import { InventoryNotificationButton } from 'macaron-frontend-ui'
import React from 'react'
import Skeleton from 'react-loading-skeleton'
import {
  useSubscribeInventoryNotificationMutation,
  useUnsubscribeInventoryNotificationMutation,
} from '~/features/guide/hooks/useSubscribeInventoryNotificationMutation'
import { useIkyuLogin } from '~/hooks/useIkyuLogin'
import { SystemError } from '~/lib/error/systemError'
import { useSwrGraphqlRequest } from '~/lib/graphql-request/swr'
import type { RestaurantId } from '~/lib/graphql/scalars'
import { GetViewerFavoriteAndSubscribeNotification } from './getViewerFavoriteAndSubscribeNotification.graphql'

type SkeletonProps = {
  containerRef: React.RefObject<HTMLDivElement>
}
const InventoryNotificationSkeleton: React.FC<SkeletonProps> = ({ containerRef }) => {
  // containerRefを渡しているのは、useIkyuLoginの中でcontainerRef.currentがnullになるため
  return (
    <div className="mb-5 flex w-full justify-center px-3" ref={containerRef}>
      {/* @ts-ignore */}
      <ikyu-login></ikyu-login>
      <Skeleton width="100%" height="20px" />
    </div>
  )
}

type Props = {
  restaurantId: RestaurantId
  withSpread?: boolean
  size?: 'xSmall' | 'small' | 'default'
  buttonClassName?: string
}

const InventoryNotificationComponent: React.FC<Props> = ({
  restaurantId,
  withSpread = false,
  size = 'default',
  buttonClassName,
}) => {
  const {
    data: viewer,
    error: viewerError,
    isLoading: viewerIsLoading,
    mutate: viewerMutate,
  } = useSwrGraphqlRequest(GetViewerFavoriteAndSubscribeNotification, {
    restaurantId,
  })
  const { subscribeTrigger, subscribeIsMutating, subscribeError } =
    useSubscribeInventoryNotificationMutation(restaurantId)
  const { unsubscribeTrigger, unsubscribeIsMutating, unsubscribeError } =
    useUnsubscribeInventoryNotificationMutation(restaurantId)

  const afterLogin = async () => {
    // ログインした後にViewer情報を更新してから空席通知登録をしに行くようにする
    await viewerMutate()
    await subscribeTrigger()
  }

  const { containerRef, openLogin } = useIkyuLogin<HTMLDivElement>({ afterLogin })

  if (viewerIsLoading) {
    return (
      <>
        <InventoryNotificationSkeleton containerRef={containerRef} />
      </>
    )
  }
  if (viewerError) {
    throw new SystemError(`GetViewerFavoriteAndSubscribeNotification ${viewerError.message ?? ''}`)
  }

  if (subscribeError) {
    throw new SystemError(`SubscribeInventoryNotification ${subscribeError.message ?? ''}`)
  }

  if (unsubscribeError) {
    throw new SystemError(`UnsubscribeInventoryNotification ${unsubscribeError.message ?? ''}`)
  }

  const memberInfo = viewer?.viewer
  const isSubscribed = viewer?.restaurant?.viewerInventoryNotificationSubscribed ?? false

  const toggleSubscribe = async () => {
    // ログインしているViewer情報を更新してから空席通知の登録・解除を行う
    await viewerMutate()
    if (isSubscribed) {
      await unsubscribeTrigger()
    } else {
      await subscribeTrigger()
    }
  }

  const handleClickButton = async () => {
    if (subscribeIsMutating || unsubscribeIsMutating) {
      return
    }

    //ログイン済みの場合、Hookで更新する
    if (memberInfo) {
      await toggleSubscribe()
    } else {
      //未ログインの場合、ログインモーダルを開く
      openLogin()
    }
  }

  return (
    <div ref={containerRef} className={buttonClassName}>
      {/* @ts-ignore */}
      <ikyu-login></ikyu-login>
      <InventoryNotificationButton
        isNotification={isSubscribed}
        size={size}
        withSpread={withSpread}
        onClick={handleClickButton}
        buttonClassName={buttonClassName}
      />
    </div>
  )
}

export default InventoryNotificationComponent
